/* =======================================================================
   Hero — Riquadro grande della home: video di sfondo + titolo + 3 bottoni
   =======================================================================
   Per cambiare il VIDEO di sfondo: sostituisci il file in
     public/assets/video/hero.mp4
   (stesso nome). Oppure cambia il percorso qui sotto.

   I 3 bottoni puntano a: /menu, telefono fisso, scheda Google del locale.
   I testi sono in src/data/copy.js → sezione "hero".
   ======================================================================= */
import { useLang } from "@/context/LanguageContext";
import { Link } from "react-router-dom";
import { asset } from "@/utils/asset";

const HERO_VIDEO = asset("/assets/video/hero.mp4");

export default function Hero({ copy }) {
  const { tr } = useLang();
  return (
    <section
      id="top"
      data-testid="hero"
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#1c1a17] text-[#F7F4EB]"
    >
      <video
        data-testid="hero-video"
        className="absolute inset-0 h-full w-full object-cover"
        src={HERO_VIDEO}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(28,26,23,0.55) 0%, rgba(28,26,23,0.35) 40%, rgba(28,26,23,0.85) 100%)",
        }}
      />
      {/* grain */}
      <div
        className="absolute inset-0 opacity-[0.08] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='200' height='200' filter='url(%23n)' opacity='0.6'/></svg>\")",
        }}
      />

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 lg:px-10 pt-40 pb-24 lg:pt-48 lg:pb-32 flex flex-col min-h-[100svh] justify-end">
        <div className="max-w-3xl">
          <p
            data-testid="hero-eyebrow"
            className="text-[0.72rem] tracking-[0.32em] uppercase text-[#E6C9A8] mb-8"
          >
            {tr(copy.hero.eyebrow)}
          </p>
          <h1 className="font-serif font-light leading-[0.92] tracking-tight text-[clamp(3rem,9vw,7.5rem)]">
            <span className="block">{tr(copy.hero.title_a)}</span>
            <span className="block italic text-[#E6C9A8]">{tr(copy.hero.title_b)}</span>
          </h1>
          <p
            data-testid="hero-sub"
            className="mt-8 max-w-xl text-lg lg:text-xl font-light leading-relaxed text-[#EDE6D6]"
          >
            {tr(copy.hero.sub)}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/menu"
              data-testid="hero-cta-menu"
              className="group inline-flex items-center gap-3 bg-[#BC472A] hover:bg-[#a93b22] text-[#F7F4EB] px-7 py-4 rounded-md text-sm uppercase tracking-[0.22em] transition-colors"
            >
              {tr(copy.hero.cta_menu)}
              <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </Link>
            <a
              href="tel:+390586790139"
              data-testid="hero-cta-call"
              className="group inline-flex items-center gap-3 bg-[#F7F4EB] hover:bg-white text-[#2C2C2A] px-7 py-4 rounded-md text-sm uppercase tracking-[0.22em] transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              {tr(copy.hero.cta_call)}
            </a>
            <a
              href="https://share.google/IjRM29RUzl3X3QDKp"
              target="_blank"
              rel="noreferrer"
              data-testid="hero-cta-visit"
              className="inline-flex items-center gap-3 border border-[#F7F4EB]/40 hover:border-[#F7F4EB] hover:bg-[#F7F4EB]/10 text-[#F7F4EB] px-7 py-4 rounded-md text-sm uppercase tracking-[0.22em] transition-colors"
            >
              {tr(copy.hero.cta_visit)}
              <span className="transition-transform duration-300 group-hover:translate-x-1">↗</span>
            </a>
          </div>
        </div>

        {/* bottom meta strip */}
        <div className="mt-16 lg:mt-24 grid grid-cols-2 lg:grid-cols-4 gap-y-6 gap-x-10 border-t border-[#F7F4EB]/15 pt-8">
          {[
            { it: "Pasta madre viva", en: "Living sourdough" },
            { it: "Grani antichi 100% italiani", en: "Ancient Italian grains" },
            { it: "Cucina senza glutine AIC", en: "AIC gluten-free kitchen" },
            { it: "Via A. Gramsci 55, Rosignano M.mo", en: "Via A. Gramsci 55, Rosignano M.mo" },
          ].map((s, i) => (
            <p
              key={i}
              className="text-[0.72rem] tracking-[0.24em] uppercase text-[#EDE6D6]/85"
            >
              {tr(s)}
            </p>
          ))}
        </div>
      </div>
    </section>
  );
}
